import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const ProjectPreview = () => {
  const [activeProject, setActiveProject] = useState(0);
  const [isHovering, setIsHovering] = useState(false);

  const projects = [
    {
      id: 1,
      title: "QuantumCommerce Platform",
      category: "E-Commerce Revolution",
      description: "Next-generation shopping experience with AI-powered recommendations and holographic product visualization.",
      image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=600&fit=crop",
      tech: ["React", "Three.js", "AI/ML", "WebGL"],
      status: "Live",
      metrics: {
        performance: "99.9%",
        users: "50K+",
        conversion: "+340%"
      },
      color: "brand-cyan"
    },
    {
      id: 2,
      title: "NeuralFlow Analytics",
      category: "Data Visualization",
      description: "Real-time data processing dashboard with interactive 3D visualizations and predictive analytics.",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=600&fit=crop",
      tech: ["D3.js", "WebGL", "Python", "TensorFlow"],
      status: "Development",
      metrics: {
        performance: "98.7%",
        dataPoints: "1M+",
        accuracy: "94.2%"
      },
      color: "brand-purple"
    },
    {
      id: 3,
      title: "CyberSecure Vault",
      category: "Security Platform",
      description: "Blockchain-based security system with quantum encryption and biometric authentication.",
      image: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&h=600&fit=crop",
      tech: ["Blockchain", "Quantum", "Biometrics", "Node.js"],
      status: "Beta",
      metrics: {
        security: "100%",
        transactions: "25K+",
        uptime: "99.99%"
      },
      color: "brand-green"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3
      }
    }
  };

  const projectVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };

  return (
    <section className="py-20 bg-background relative overflow-hidden">
      {/* Background Neural Network */}
      <div className="absolute inset-0 opacity-10">
        <svg className="w-full h-full" viewBox="0 0 1000 1000">
          <defs>
            <pattern id="neural-grid" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
              <circle cx="50" cy="50" r="2" fill="currentColor" className="text-brand-cyan">
                <animate attributeName="opacity" values="0.3;1;0.3" dur="3s" repeatCount="indefinite" />
              </circle>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#neural-grid)" />
        </svg>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full glass-morphism cyber-border mb-6">
            <Icon name="Layers" size={16} className="text-brand-purple" />
            <span className="font-mono text-sm text-brand-purple">PROJECT DIMENSIONS</span>
          </div>
          
          <h2 className="font-orbitron font-bold text-3xl md:text-4xl lg:text-5xl text-foreground mb-4">
            Featured <span className="text-brand-purple">Innovations</span>
          </h2>
          
          <p className="font-inter text-lg text-text-secondary max-w-2xl mx-auto">
            Explore cutting-edge projects that push the boundaries of web technology and user experience.
          </p>
        </motion.div>

        {/* Project Showcase */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Project Navigation */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="space-y-6"
          >
            {projects.map((project, index) => (
              <motion.div
                key={project.id}
                variants={projectVariants}
                className={`cursor-pointer transition-all duration-300 ${
                  activeProject === index ? 'scale-105' : 'hover:scale-102'
                }`}
                onClick={() => setActiveProject(index)}
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
              >
                <div className={`glass-morphism rounded-xl p-6 cyber-border transition-all duration-300 ${
                  activeProject === index 
                    ? 'cyber-border-active cyber-glow' :'hover:cyber-border-active'
                }`}>
                  {/* Project Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="font-orbitron font-bold text-lg text-foreground">
                          {project.title}
                        </h3>
                        <div className={`px-2 py-1 rounded-full text-xs font-mono ${
                          project.status === 'Live' ?'bg-brand-green/20 text-brand-green' 
                            : project.status === 'Beta' ?'bg-brand-orange/20 text-brand-orange' :'bg-brand-purple/20 text-brand-purple'
                        }`}>
                          {project.status}
                        </div>
                      </div>
                      <p className="font-inter text-sm text-brand-cyan mb-2">
                        {project.category}
                      </p>
                      <p className="font-inter text-sm text-text-secondary leading-relaxed">
                        {project.description}
                      </p>
                    </div>
                  </div>

                  {/* Tech Stack */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {project.tech.map((tech, techIndex) => (
                      <span
                        key={techIndex}
                        className="px-2 py-1 rounded-md bg-surface/50 text-xs font-mono text-text-tertiary"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>

                  {/* Metrics */}
                  <div className="grid grid-cols-3 gap-4">
                    {Object.entries(project.metrics).map(([key, value]) => (
                      <div key={key} className="text-center">
                        <div className={`font-orbitron font-bold text-sm text-${project.color}`}>
                          {value}
                        </div>
                        <div className="font-mono text-xs text-text-tertiary capitalize">
                          {key}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Project Preview */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative"
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={activeProject}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.5 }}
                className="relative group"
              >
                {/* Main Preview */}
                <div className="relative overflow-hidden rounded-2xl cyber-border hover:cyber-border-active transition-all duration-300 cyber-hover">
                  <div className="aspect-video relative">
                    <Image
                      src={projects[activeProject].image}
                      alt={projects[activeProject].title}
                      className="w-full h-full object-cover"
                    />
                    
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent"></div>
                    
                    {/* Floating Elements */}
                    <div className="absolute top-4 right-4">
                      <div className={`w-3 h-3 bg-${projects[activeProject].color} rounded-full animate-pulse`}></div>
                    </div>
                    
                    {/* Bottom Info */}
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-orbitron font-bold text-lg text-foreground mb-1">
                            {projects[activeProject].title}
                          </h4>
                          <p className="font-mono text-sm text-text-secondary">
                            {projects[activeProject].category}
                          </p>
                        </div>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          className="cyber-border hover:cyber-border-active hover:cyber-glow"
                          iconName="ExternalLink"
                          iconPosition="right"
                          iconSize={16}
                        >
                          View
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Holographic Frame Effect */}
                <div className="absolute -inset-4 bg-gradient-to-r from-brand-cyan/20 via-transparent to-brand-purple/20 rounded-3xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity duration-300"></div>
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5, duration: 0.8 }}
          className="text-center mt-16"
        >
          <Link to="/project-dimensions-portfolio">
            <Button
              variant="outline"
              size="lg"
              className="cyber-border hover:cyber-border-active hover:cyber-glow transition-all duration-300"
              iconName="ArrowRight"
              iconPosition="right"
              iconSize={20}
            >
              Explore All Projects
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default ProjectPreview;